"use client";
import { usePublicT } from "../../public-locale";

import {
  ArrowRight,
  BadgeCheck,
  Check,
  Database,
  Layers3,
  ListChecks,
  MapPinCheck,
  Plus,
  ShieldCheck,
} from "lucide-react";

import Link from "../components/MarketingLink";
import CheckoutButton from "../components/CheckoutButton";
import PageShell from "../components/PageShell";
import ProductPreview from "../components/ProductPreview";
import { useState, useEffect, useRef } from "react";
import Logo from "../components/Logo";
import { useTheme } from "../contexts/ThemeContext";
import { asset } from "../assets";
import { marketingIcon } from "../components/MarketingIcon";
import { IndustryIcon } from "../industries/IndustryIcon";
import type { IndustryId } from "../industries/navigation";
import { MARKETING_PLANS } from "../config/marketing-pricing";
const logoGoogle = asset("Logo_Google.svg");
const logoMeta = asset("Logo_Meta.svg");
const logoFacebook = asset("Logo_Facebook.svg");
const logoInstagram = asset("Logo_Instagram.svg");
const logoWhatsapp = asset("Logo_Whatsapp.svg");
const logoBooking = asset("Logo_Booking.svg");
const logoTripadvisor = asset("Logo_tripadvisor.svg");
const logoApple = asset("Logo_Apple.svg");
const logoYelp = asset("Logo_yelp.svg");

const LOCAL_BUSINESS_PHOTOS = [
  { src: "/marketing-assets/local-business-restaurant.jpg", alt: "Independent restaurant" },
  { src: "/marketing-assets/local-business-salon.jpg", alt: "Local salon" },
  { src: "/marketing-assets/local-business-hotel.jpg", alt: "Hospitality business" },
  { src: "/marketing-assets/local-business-clinic.jpg", alt: "Healthcare reception" },
  { src: "/marketing-assets/local-business-retail.jpg", alt: "Independent retail boutique" },
];

const HOME_PRICING_PLANS = MARKETING_PLANS.map((plan) => ({
  plan: plan.name,
  price: plan.name === "Enterprise" ? "Custom" : `$${plan.monthly}`,
  period: plan.name === "Enterprise" ? "" : "/mo",
  desc: plan.previewDescription,
  highlight: plan.highlight,
}));


/* ── Ecosystem section ─ */

const ECOSYSTEM = [
  { name: "Google", logo: logoGoogle, angle: 0 },
  { name: "Meta", logo: logoMeta, angle: 40 },
  { name: "Facebook", logo: logoFacebook, angle: 80 },
  { name: "Instagram", logo: logoInstagram, angle: 120 },
  { name: "WhatsApp", logo: logoWhatsapp, angle: 160 },
  { name: "Booking.com", logo: logoBooking, angle: 200 },
  { name: "Tripadvisor", logo: logoTripadvisor, angle: 240 },
  { name: "Apple", logo: logoApple, angle: 280 },
  { name: "Yelp", logo: logoYelp, angle: 320 },
];

function EcosystemSection() {
  const t = usePublicT();
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const { effective } = useTheme();
  const isDark = effective === "dark";
  const CX = 400, CY = 240, R = 185;

  return (
    <section className="py-28 overflow-hidden relative">
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(ellipse 80% 60% at 50% 50%, rgba(77,0,211,0.06) 0%, transparent 70%)",
      }} />

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16 reveal">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest mb-6"
            style={{ background: "rgba(77,0,211,0.08)", color: "#4D00D3", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("Connected Ecosystem")}</div>
          <h2 className="text-4xl md:text-5xl font-bold mb-5 leading-tight"
            style={{ fontFamily: "'Sora', sans-serif", color: "var(--foreground)" }}>{t("One platform.")}<br />{t("Every channel that matters.")}</h2>
          <p className="text-lg max-w-xl mx-auto"
            style={{ color: "var(--muted)", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("Your business at the center. Discovery, reputation, messaging and customer signals come together across the V1 provider ecosystem.")}</p>
        </div>

        {/* Desktop constellation (lg+) */}
        <div className="hidden lg:flex justify-center">
          <div className="relative" style={{ width: 800, height: 480 }}>
            <svg className="absolute inset-0 pointer-events-none" width="800" height="480" viewBox="0 0 800 480">
              {t(ECOSYSTEM.map((c, i) => {
                const rad = ((c.angle - 90) * Math.PI) / 180;
                const x = CX + R * Math.cos(rad);
                const y = CY + R * Math.sin(rad);
                const isHov = hoveredIdx === i;
                const delay = i * 0.65;
                const revDelay = i * 0.65 + 5;
                return (
                  <g key={i}>
                    <line
                      x1={CX} y1={CY} x2={x} y2={y}
                      stroke={isHov ? "rgba(77,0,211,0.48)" : "rgba(77,0,211,0.13)"}
                      strokeWidth={isHov ? "1.5" : "1"}
                      strokeDasharray="5 4"
                      style={{
                        transition: "stroke 280ms ease, stroke-width 280ms ease",
                        animation: `dash-flow ${2.8 + i * 0.18}s linear infinite`,
                      }}
                    />
                    <circle r="2.5" fill="#4D00D3" opacity="0">
                      <animate attributeName="cx" from={String(CX)} to={String(x)} dur="2.2s" begin={`${delay}s`} repeatCount="indefinite" calcMode="linear" />
                      <animate attributeName="cy" from={String(CY)} to={String(y)} dur="2.2s" begin={`${delay}s`} repeatCount="indefinite" calcMode="linear" />
                      <animate attributeName="opacity" values="0;0;0.85;0.85;0" keyTimes="0;0.05;0.15;0.88;1" dur="2.2s" begin={`${delay}s`} repeatCount="indefinite" />
                    </circle>
                    <circle r="2" fill="#8B3DFF" opacity="0">
                      <animate attributeName="cx" from={String(x)} to={String(CX)} dur="2.6s" begin={`${revDelay}s`} repeatCount="indefinite" calcMode="linear" />
                      <animate attributeName="cy" from={String(y)} to={String(CY)} dur="2.6s" begin={`${revDelay}s`} repeatCount="indefinite" calcMode="linear" />
                      <animate attributeName="opacity" values="0;0;0.6;0.6;0" keyTimes="0;0.05;0.15;0.88;1" dur="2.6s" begin={`${revDelay}s`} repeatCount="indefinite" />
                    </circle>
                  </g>
                );
              }))}
            </svg>

            {/* Center hub */}
            <div className="absolute z-10" style={{ left: CX, top: CY, transform: "translate(-50%, -50%)" }}>
              <div style={{
                width: 82, height: 82, borderRadius: "50%",
                background: "var(--surface)",
                border: "2px solid rgba(77,0,211,0.22)",
                boxShadow: "0 0 0 14px rgba(77,0,211,0.05), 0 0 0 28px rgba(77,0,211,0.025), 0 8px 32px rgba(77,0,211,0.14)",
                display: "flex", alignItems: "center", justifyContent: "center",
                animation: "float 3.8s ease-in-out infinite",
              }}>
                <Logo compact className="w-10 h-10" />
              </div>
              <span className="block text-center mt-2 text-xs font-semibold"
                style={{ color: "#4D00D3", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("Your Business")}</span>
            </div>

            {/* Connector cards */}
            {t(ECOSYSTEM.map((c, i) => {
              const rad = ((c.angle - 90) * Math.PI) / 180;
              const x = CX + R * Math.cos(rad);
              const y = CY + R * Math.sin(rad);
              const isHov = hoveredIdx === i;
              return (
                <div
                  key={i}
                  className="absolute reveal"
                  style={{
                    left: x, top: y,
                    transform: `translate(-50%, -50%) translateY(${isHov ? "-4px" : "0"})`,
                    transition: "transform 260ms cubic-bezier(.22,1,.36,1)",
                    transitionDelay: `${i * 0.06}s`,
                    zIndex: 10,
                  }}
                  onMouseEnter={() => setHoveredIdx(i)}
                  onMouseLeave={() => setHoveredIdx(null)}
                >
                  <div style={{
                    width: 62, height: 62, borderRadius: 16,
                    background: "var(--surface)",
                    border: `1px solid ${isHov ? "rgba(77,0,211,0.28)" : "var(--border)"}`,
                    boxShadow: isHov
                      ? "0 8px 24px rgba(77,0,211,0.14), 0 2px 8px rgba(0,0,0,0.07)"
                      : "0 2px 10px rgba(0,0,0,0.05)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: 11,
                    transition: "box-shadow 260ms ease, border-color 260ms ease",
                  }}>
                    <img
                      src={c.logo}
                      alt={t(c.name)}
                      draggable={false}
                      style={{
                        width: "100%", height: "100%",
                        objectFit: "contain",
                        transform: isHov ? "scale(1.04)" : "scale(1)",
                        transition: "transform 260ms cubic-bezier(.22,1,.36,1)",
                        filter: c.name === "Apple" && isDark ? "invert(1)" : "none",
                      }}
                    />
                  </div>
                  <span className="block text-center mt-1.5"
                    style={{ fontSize: 10, fontFamily: "'Plus Jakarta Sans', sans-serif", color: "var(--muted)", whiteSpace: "nowrap" }}>
                    {t(c.name)}
                  </span>
                </div>
              );
            }))}
          </div>
        </div>

        {/* Mobile / tablet grid */}
        <div className="lg:hidden space-y-8">
          <div className="flex flex-col items-center">
            <div style={{
              width: 64, height: 64, borderRadius: "50%",
              background: "var(--surface)",
              border: "2px solid rgba(77,0,211,0.22)",
              boxShadow: "0 0 0 10px rgba(77,0,211,0.05), 0 4px 20px rgba(77,0,211,0.12)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Logo compact className="w-8 h-8" />
            </div>
            <span className="mt-2 text-xs font-semibold" style={{ color: "#4D00D3", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("Your Business")}</span>
          </div>
          <div className="grid grid-cols-3 gap-5 max-w-xs mx-auto">
            {t(ECOSYSTEM.map((c, i) => (
              <div key={i} className="flex flex-col items-center gap-1.5 reveal" style={{ transitionDelay: `${i * 0.05}s` }}>
                <div style={{
                  width: 56, height: 56, borderRadius: 14,
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  padding: 9,
                }}>
                  <img
                    src={c.logo} alt={t(c.name)} draggable={false}
                    style={{
                      width: "100%", height: "100%", objectFit: "contain",
                      filter: c.name === "Apple" && isDark ? "invert(1)" : "none",
                    }}
                  />
                </div>
                <span style={{ fontSize: 9, fontFamily: "'Plus Jakarta Sans', sans-serif", color: "var(--muted)", textAlign: "center" }}>
                  {t(c.name)}
                </span>
              </div>
            )))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Outcomes section ────────────────────────────────────── */

function OutcomesSection() {
  const t = usePublicT();
  const [hovered, setHovered] = useState<number | null>(null);

  const items = [
    {
      icon: MapPinCheck,
      title: "Get found",
      desc: "Strengthen how your business appears across supported local discovery signals and understand where visibility deserves attention.",
    },
    {
      icon: BadgeCheck,
      title: "Build trust",
      desc: "Turn every review into a growth signal. Respond intelligently, manage reputation, and convert trust into loyalty.",
    },
    {
      icon: ListChecks,
      title: "Know what to do next",
      desc: "AI-powered priority actions tell you exactly what matters today, not just what changed.",
    },
  ];

  return (
    <section className="py-28 relative">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "var(--surface-2)" }} />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="text-center mb-16 reveal">
          <h2
            className="text-3xl md:text-4xl font-bold leading-tight"
            style={{
              fontFamily: "'Sora', sans-serif",
              color: "var(--foreground)",
              letterSpacing: "-0.02em",
            }}
          >{t("Three outcomes that change your business.")}</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {t(items.map((item, i) => {
            const isHov = hovered === i;
            const Icon = item.icon;
            return (
              <div
                key={item.title}
                className={`reveal reveal-delay-${i + 1} p-8 rounded-3xl`}
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                  boxShadow: isHov
                    ? "0 12px 40px rgba(77,0,211,0.12), 0 4px 16px rgba(0,0,0,0.08)"
                    : "var(--shadow-sm)",
                  transform: isHov ? "translateY(-3px)" : "translateY(0)",
                  transition: "transform 250ms cubic-bezier(.22,1,.36,1), box-shadow 250ms cubic-bezier(.22,1,.36,1)",
                }}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
              >
                <div
                  className="mb-6 w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{
                    color: "#4D00D3",
                    background: isHov
                      ? "linear-gradient(135deg, rgba(77,0,211,0.14), rgba(139,61,255,0.08))"
                      : "rgba(77,0,211,0.08)",
                    border: "1px solid rgba(77,0,211,0.10)",
                    boxShadow: isHov ? "0 10px 24px rgba(77,0,211,0.12)" : "none",
                    transform: isHov ? "translateY(-2px)" : "translateY(0)",
                    transition: "all 250ms cubic-bezier(.22,1,.36,1)",
                  }}
                >
                  <Icon aria-hidden="true" size={24} strokeWidth={1.75} absoluteStrokeWidth />
                </div>
                <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: "'Sora', sans-serif", color: "var(--foreground)" }}>
                  {t(item.title)}
                </h3>
                <p className="text-base leading-relaxed" style={{ color: "var(--muted)", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {t(item.desc)}
                </p>
              </div>
            );
          }))}
        </div>
      </div>
    </section>
  );
}

/* ── Count-up utility ────────────────────────────────────── */

function CountUp({ value, duration = 1400, delay = 0 }: { value: string; duration?: number; delay?: number }) {
  const t = usePublicT();
  const [display, setDisplay] = useState("–");
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) { setDisplay(value); return; }

    // parse: optional leading "+", digits (with optional decimal), then suffix
    const m = value.match(/^([+]?)(\d+(?:\.\d+)?)(.*)$/);
    if (!m) { setDisplay(value); return; }
    const prefix = m[1] ?? "";
    const numStr = m[2] ?? "0";
    const suffix = m[3] ?? "";
    const target = parseFloat(numStr);
    const decimals = (numStr.split(".")[1] ?? "").length;

    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (entry?.isIntersecting && !started.current) {
        started.current = true;
        setTimeout(() => {
          const startTime = performance.now();
          const tick = (now: number) => {
            const t = Math.min((now - startTime) / duration, 1);
            const eased = 1 - Math.pow(1 - t, 3);
            const current = eased * target;
            setDisplay(prefix + current.toFixed(decimals) + suffix);
            if (t < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }, delay);
      }
    }, { threshold: 0.5 });

    observer.observe(el);
    return () => observer.disconnect();
  }, [value, duration, delay]);

  return <span ref={ref}>{t(display)}</span>;
}

/* ── Feature pillars ─────────────────────────────────────── */

function FeaturePillars() {
  const t = usePublicT();
  const pillars = [
    {
      tag: "Intelligence",
      title: "Your Local Score",
      desc: "A history-aware intelligence view built from supported visibility, reputation and performance signals available for your location.",
      link: "/local-score",
      metric: "87",
      metricLabel: "Local Score",
    },
    {
      tag: "Reputation",
      title: "Reviews that build trust",
      desc: "Bring supported V1 reputation activity into one workflow across the provider ecosystem, with AI-assisted context and operator-controlled responses.",
      link: "/reviews",
      metric: "4.8★",
      metricLabel: "Reputation workflow",
    },
    {
      tag: "Visibility",
      title: "Be found everywhere",
      desc: "Track how you appear in local search, Maps, and AI-driven discovery. Know your ranking, understand competitors, fix what's holding you back.",
      link: "/local-seo",
      metric: "90d",
      metricLabel: "Visibility context",
    },
    {
      tag: "AI",
      title: "Optrixis AI",
      desc: "An intelligence layer that explains supported signals, surfaces context, and helps operators decide what deserves attention next.",
      link: "/ai",
      metric: "12",
      metricLabel: "Assisted context",
    },
  ];

  return (
    <section className="py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16 reveal">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest mb-6" style={{ background: "rgba(77,0,211,0.08)", color: "#4D00D3", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("What Optrixis Local does")}</div>
          <h2 className="text-4xl md:text-5xl font-bold mb-5" style={{ fontFamily: "'Sora', sans-serif", color: "var(--foreground)" }}>{t("Everything that drives")}<br />{t("local growth, unified.")}</h2>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {t(pillars.map((p, i) => (
            <Link
              key={p.title} href={p.link}
              className={`reveal reveal-delay-${i + 1} group block p-8 rounded-3xl transition-all duration-300 card-hover`}
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                textDecoration: "none",
              }}
            >
              <div className="flex items-start justify-between mb-6">
                <span
                  className="text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full"
                  style={{ background: "rgba(77,0,211,0.08)", color: "#4D00D3", fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: "0.1em" }}
                >
                  {t(p.tag)}
                </span>
                <div className="text-right">
                  <div className="text-2xl font-bold text-gradient" style={{ fontFamily: "'Sora', sans-serif" }}>
                    <CountUp value={p.metric} duration={1300} delay={i * 120} />
                  </div>
                  <div className="text-xs" style={{ color: "var(--muted)", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t(p.metricLabel)}</div>
                </div>
              </div>
              <h3 className="text-xl font-bold mb-3 group-hover:text-brand transition-colors" style={{ fontFamily: "'Sora', sans-serif", color: "var(--foreground)" }}>
                {t(p.title)}
              </h3>
              <p className="text-sm leading-relaxed mb-4" style={{ color: "var(--muted)", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {t(p.desc)}
              </p>
              <span className="text-sm font-semibold text-brand flex items-center gap-1 group-hover:gap-2 transition-all">{t("Learn more")}<ArrowRight aria-hidden="true" size={14} strokeWidth={1.8} absoluteStrokeWidth />
              </span>
            </Link>
          )))}
        </div>
      </div>
    </section>
  );
}

/* ── Before/After section ────────────────────────────────── */

function TransformationSection() {
  const t = usePublicT();
  return (
    <section className="py-28 relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "linear-gradient(135deg, rgba(77,0,211,0.04) 0%, transparent 60%)",
        }}
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="reveal">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest mb-8" style={{ background: "rgba(77,0,211,0.08)", color: "#4D00D3", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("The transformation")}</div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight" style={{ fontFamily: "'Sora', sans-serif", color: "var(--foreground)" }}>{t("From scattered signals to clear decisions.")}</h2>
            <p className="text-lg leading-relaxed mb-8" style={{ color: "var(--muted)", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("Local growth signals are spread across reviews, discovery, social and customer channels. Optrixis Local brings them into one operating view so teams can understand what changed and what deserves attention next.")}</p>
            <Link href="/platform" className="btn-primary inline-flex items-center gap-2">{t("See the platform")}<ArrowRight aria-hidden="true" size={14} strokeWidth={1.8} absoluteStrokeWidth />
            </Link>
          </div>

          <div className="reveal reveal-delay-2">
            <div className="grid gap-4">
              {t([
                {
                  before: "Checking supported review channels manually",
                  after: "Supported review activity in one workflow",
                  icon: marketingIcon("star"),
                },
                {
                  before: "Not knowing why traffic dropped",
                  after: "AI explains changes in plain language",
                  icon: marketingIcon("chart"),
                },
                {
                  before: "Guessing what to improve",
                  after: "Ranked priority actions every morning",
                  icon: marketingIcon("arrow"),
                },
                {
                  before: "No idea how you compare locally",
                  after: "History-aware Local Score context",
                  icon: marketingIcon("target"),
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="grid grid-cols-2 gap-4 items-center"
                >
                  <div className="p-4 rounded-2xl" style={{ background: "rgba(239,68,68,0.05)", border: "1px solid rgba(239,68,68,0.12)" }}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold uppercase tracking-wide text-red-400" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("Before")}</span>
                    </div>
                    <p className="text-xs leading-relaxed" style={{ color: "var(--muted)", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t(item.before)}</p>
                  </div>
                  <div className="p-4 rounded-2xl" style={{ background: "rgba(77,0,211,0.06)", border: "1px solid rgba(77,0,211,0.15)" }}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold uppercase tracking-wide" style={{ color: "#4D00D3", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("With Optrixis")}</span>
                    </div>
                    <p className="text-xs leading-relaxed" style={{ color: "var(--foreground)", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t(item.after)}</p>
                  </div>
                </div>
              )))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── AI section ──────────────────────────────────────────── */

const AI_MESSAGES = [
  { role: "you", text: "Why did my visibility drop this week?" },
  {
    role: "ai",
    text: "Your Google Business Profile photo activity changed from its recent pattern. Fresh, accurate photos may be worth reviewing alongside the supported visibility signals before taking action.",
  },
  { role: "you", text: "What's the highest impact thing I can do today?" },
  {
    role: "ai",
    text: "A supported review signal needs attention. I can show the context and prepare a response draft for operator review.",
  },
] as const;

// Cumulative delays (ms) at which each bubble becomes visible
const BUBBLE_DELAYS = [350, 1550, 3100, 4400];
const LOOP_RESET_DELAY = 8600; // after final bubble, hold then restart
const RESET_PAUSE = 650;       // ms to hold fully hidden before replaying

function AIChatCard() {
  const t = usePublicT();
  const [phase, setPhase] = useState(0); // 0 = all hidden, 1–4 = bubbles visible

  useEffect(() => {
    let alive = true;
    const timers: ReturnType<typeof setTimeout>[] = [];

    const schedule = (fn: () => void, delay: number) => {
      const t = setTimeout(() => { if (alive) fn(); }, delay);
      timers.push(t);
    };

    const run = () => {
      BUBBLE_DELAYS.forEach((d, i) => {
        schedule(() => setPhase(i + 1), d);
      });
      schedule(() => {
        setPhase(0);
        schedule(run, RESET_PAUSE);
      }, LOOP_RESET_DELAY);
    };

    run();
    return () => { alive = false; timers.forEach(clearTimeout); };
  }, []);

  return (
    <div
      className="rounded-3xl p-6 highlight-border"
      style={{
        background: "var(--surface)",
        border: "1px solid rgba(77,0,211,0.15)",
        boxShadow: "var(--shadow-xl)",
      }}
    >
      {/* Header with favicon */}
      <div className="flex items-center gap-3 mb-6">
        <Logo compact className="w-8 h-8" />
        <div>
          <div className="text-sm font-bold" style={{ color: "var(--foreground)", fontFamily: "'Sora', sans-serif" }}>{t("Optrixis AI")}</div>
          <div className="text-xs" style={{ color: "var(--muted)", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("Your local growth advisor")}</div>
        </div>
      </div>

      {/* Chat bubbles */}
      <div className="space-y-4" style={{ minHeight: 220 }}>
        {t(AI_MESSAGES.map((msg, i) => {
          const visible = phase > i;
          return (
            <div
              key={i}
              className={`flex ${msg.role === "you" ? "justify-end" : "justify-start"}`}
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(10px)",
                transition: "opacity 420ms cubic-bezier(.22,1,.36,1), transform 420ms cubic-bezier(.22,1,.36,1)",
              }}
            >
              <div
                className="max-w-xs px-4 py-2.5 text-sm leading-relaxed"
                style={{
                  background: msg.role === "ai" ? "rgba(77,0,211,0.08)" : "var(--surface-2)",
                  color: "var(--foreground)",
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  borderRadius: msg.role === "you" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                }}
              >
                {t(msg.text)}
              </div>
            </div>
          );
        }))}
      </div>
    </div>
  );
}

function AISection() {
  const t = usePublicT();
  return (
    <section className="py-28 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "var(--surface-2)" }} />
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] pointer-events-none"
        style={{
          background: "radial-gradient(ellipse at center, rgba(77,0,211,0.12) 0%, transparent 70%)",
          filter: "blur(40px)",
        }}
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="reveal order-2 lg:order-1">
            <AIChatCard />
          </div>

          <div className="reveal reveal-delay-2 order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest mb-8"
              style={{ background: "rgba(77,0,211,0.08)", color: "#4D00D3", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("Optrixis AI")}</div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight"
              style={{ fontFamily: "'Sora', sans-serif", color: "var(--foreground)" }}>{t("An intelligence layer that thinks with you.")}</h2>
            <p className="text-lg leading-relaxed mb-6"
              style={{ color: "var(--muted)", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("Optrixis AI interprets supported signals, adds context to meaningful changes, and helps operators understand what may deserve attention next.")}</p>
            <ul className="space-y-3 mb-8">
              {t([
                "Plain-language explanations of every metric change",
                "Personalized priority actions ranked by impact",
                "AI-drafted review responses in your brand voice",
                "Competitive gap analysis with actionable guidance",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span
                    className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                    aria-hidden="true"
                    style={{ background: "rgba(77,0,211,0.12)", color: "#4D00D3" }}
                  >
                    <Check size={12} strokeWidth={2} absoluteStrokeWidth />
                  </span>
                  <span className="text-sm" style={{ color: "var(--foreground)", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t(item)}</span>
                </li>
              )))}
            </ul>
            <Link href="/ai" className="btn-primary inline-flex items-center gap-2">{t("Explore Optrixis AI")}<ArrowRight aria-hidden="true" size={14} strokeWidth={1.8} absoluteStrokeWidth />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Pricing preview ─────────────────────────────────────── */

function PricingPreview() {
  const t = usePublicT();
  return (
    <section className="py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-14 reveal">
          <h2 className="text-4xl md:text-5xl font-bold mb-5" style={{ fontFamily: "'Sora', sans-serif", color: "var(--foreground)" }}>{t("Simple, honest pricing.")}</h2>
          <p className="text-lg max-w-lg mx-auto" style={{ color: "var(--muted)", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("One subscription = one real physical business location. Clear plans. Transparent limits. No hidden surprises.")}</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          {t(HOME_PRICING_PLANS.map((p, i) => (
            <div
              key={p.plan}
              className={`reveal reveal-delay-${i + 1} p-6 rounded-3xl flex flex-col`}
              style={{
                background: p.highlight ? "#4D00D3" : "var(--surface)",
                border: p.highlight ? "none" : "1px solid var(--border)",
                boxShadow: p.highlight ? "var(--shadow-brand)" : "var(--shadow-sm)",
              }}
            >
              <div className="mb-5">
                <div
                  className="text-xs font-semibold uppercase tracking-widest mb-3"
                  style={{ color: p.highlight ? "rgba(255,255,255,0.7)" : "var(--muted)", fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                  {t(p.plan)}
                </div>
                <div className="flex items-baseline gap-0.5">
                  <span className="text-3xl font-bold" style={{ color: p.highlight ? "white" : "var(--foreground)", fontFamily: "'Sora', sans-serif" }}>{t(p.price)}</span>
                  <span className="text-sm" style={{ color: p.highlight ? "rgba(255,255,255,0.6)" : "var(--muted)" }}>{t(p.period)}</span>
                </div>
              </div>
              <p className="text-sm leading-relaxed flex-1 mb-6" style={{ color: p.highlight ? "rgba(255,255,255,0.75)" : "var(--muted)", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {t(p.desc)}
              </p>
              <CheckoutButton plan={p.plan} highlight={p.highlight} preview />
            </div>
          )))}
        </div>

        <p className="text-center text-sm" style={{ color: "var(--muted)", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("A 14-day trial is available with controlled product entitlements.")}{t(" ")}
          <Link href="/pricing" className="text-brand font-semibold hover:underline inline-flex items-center gap-1.5">{t("View full pricing")}<ArrowRight aria-hidden="true" size={14} strokeWidth={1.8} absoluteStrokeWidth /></Link>
        </p>
      </div>
    </section>
  );
}

/* ── Industry use cases preview ──────────────────────────── */

function IndustriesPreview() {
  const t = usePublicT();
  const [hovered, setHovered] = useState<number | null>(null);

  const industries = [
    { id: "restaurants" as IndustryId, name: "Restaurants", href: "/industries/restaurants", desc: "Turn online presence into full tables." },
    { id: "salons-spas" as IndustryId, name: "Salons & Spas", href: "/industries/salons-spas", desc: "Build the reputation that fills your calendar." },
    { id: "hotels" as IndustryId, name: "Hotels", href: "/industries/hotels", desc: "Stand out where travelers search first." },
    { id: "healthcare" as IndustryId, name: "Healthcare", href: "/industries/healthcare", desc: "Trust and visibility for clinics and practices." },
    { id: "retail" as IndustryId, name: "Retail", href: "/industries/retail", desc: "Drive foot traffic with local intelligence." },
  ];

  return (
    <section className="py-20 relative" style={{ background: "var(--surface-2)" }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12 reveal">
          <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: "'Sora', sans-serif", color: "var(--foreground)" }}>{t("Built for real local businesses.")}</h2>
          <p className="text-base" style={{ color: "var(--muted)", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("Industry-specific intelligence. Sector-aware benchmarks. Insights that fit how your business actually works.")}</p>
        </div>
        <div className="flex flex-wrap justify-center gap-4">
          {t(industries.map((ind, i) => {
            const isHov = hovered === i;
            return (
              <Link
                key={ind.name} href={ind.href}
                className={`reveal reveal-delay-${i + 1} flex items-center gap-3 px-5 py-3.5 rounded-2xl`}
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                  textDecoration: "none",
                  boxShadow: isHov ? "0 8px 24px rgba(77,0,211,0.1), 0 2px 8px rgba(0,0,0,0.07)" : "var(--shadow-sm)",
                  transform: isHov ? "translateY(-3px)" : "translateY(0)",
                  transition: "transform 240ms cubic-bezier(.22,1,.36,1), box-shadow 240ms ease",
                }}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
              >
                <div
                  style={{
                    width: 38,
                    height: 38,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 12,
                    color: "#4D00D3",
                    background: isHov ? "rgba(77,0,211,0.12)" : "rgba(77,0,211,0.07)",
                    border: "1px solid rgba(77,0,211,0.10)",
                    transform: isHov ? "scale(1.04)" : "scale(1)",
                    transition: "all 240ms cubic-bezier(.22,1,.36,1)",
                  }}
                >
                  <IndustryIcon id={ind.id} className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-sm font-semibold" style={{ color: "var(--foreground)", fontFamily: "'Sora', sans-serif" }}>{t(ind.name)}</div>
                  <div className="text-xs" style={{ color: "var(--muted)", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t(ind.desc)}</div>
                </div>
              </Link>
            );
          }))}
        </div>
      </div>
    </section>
  );
}

/* ── Trust section ───────────────────────────────────────── */

function TrustSection() {
  const t = usePublicT();
  return (
    <section className="py-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="rounded-3xl p-10 md:p-14 reveal" style={{ background: "var(--surface)", border: "1px solid var(--border)", boxShadow: "var(--shadow-sm)" }}>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: "'Sora', sans-serif", color: "var(--foreground)" }}>{t("Built on trust and transparency.")}</h2>
              <p className="text-base leading-relaxed mb-6" style={{ color: "var(--muted)", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("Your business data belongs to you. We never sell it, never share it, and give you full control over what we store. Enterprise-grade security as a default — not an add-on.")}</p>
              <Link href="/security" className="btn-ghost inline-flex items-center gap-2 text-sm">{t("Security & Trust")}<ArrowRight aria-hidden="true" size={14} strokeWidth={1.8} absoluteStrokeWidth />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {t([
                { icon: Layers3, label: "Tenant isolation", sub: "Multi-tenant controls" },
                { icon: ShieldCheck, label: "GDPR readiness", sub: "Privacy controls" },
                { icon: ListChecks, label: "Audit trails", sub: "Traceable operations" },
                { icon: Database, label: "Data ownership", sub: "Always yours" },
              ].map((trust) => {
                const Icon = trust.icon;
                return (
                <div key={trust.label} className="p-4 rounded-2xl" style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}>
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center mb-3"
                    style={{ color: "#4D00D3", background: "rgba(77,0,211,0.08)", border: "1px solid rgba(77,0,211,0.10)" }}
                  >
                    <Icon aria-hidden="true" size={18} strokeWidth={1.75} absoluteStrokeWidth />
                  </div>
                  <div className="text-sm font-semibold mb-0.5" style={{ color: "var(--foreground)", fontFamily: "'Sora', sans-serif" }}>{t(trust.label)}</div>
                  <div className="text-xs" style={{ color: "var(--muted)", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t(trust.sub)}</div>
                </div>
                );
              }))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── FAQ preview ─────────────────────────────────────────── */

function FAQPreview() {
  const t = usePublicT();
  const [open, setOpen] = useState<number | null>(null);
  const items = [
    {
      q: "What does 'one location' mean?",
      a: "Each subscription covers one physical business address. The connected platforms (Google, Meta, Instagram, etc.) are integrations — not extra locations. Enterprise plans cover multiple physical locations.",
    },
    {
      q: "Which integrations are included in V1?",
      a: "The V1 provider ecosystem includes Google Business Profile, Meta, Facebook, Instagram, WhatsApp, Tripadvisor, Booking.com, Apple and Yelp. Exact capabilities can vary by provider API, account eligibility, region, permissions and policy.",
    },
    {
      q: "Do I need technical knowledge to use Optrixis Local?",
      a: "Not at all. Optrixis Local is designed for business owners and managers, not technical teams. Guided onboarding keeps provider connection and setup steps clear inside the product.",
    },
    {
      q: "Is there a free trial?",
      a: "A 14-day trial is available with controlled trial entitlements.",
    },
  ];

  return (
    <section className="py-20" style={{ background: "var(--surface-2)" }}>
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12 reveal">
          <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: "'Sora', sans-serif", color: "var(--foreground)" }}>{t("Common questions.")}</h2>
        </div>
        <div className="space-y-3">
          {t(items.map((item, i) => (
            <div
              key={i}
              className="reveal rounded-2xl overflow-hidden transition-all"
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                transitionDelay: `${i * 0.05}s`,
              }}
            >
              <button
                className="w-full text-left px-6 py-5 flex items-center justify-between gap-4"
                onClick={() => setOpen(open === i ? null : i)}
                style={{ color: "var(--foreground)", fontFamily: "'Sora', sans-serif" }}
              >
                <span className="font-semibold text-base">{t(item.q)}</span>
                <span
                  className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-transform duration-200"
                  style={{
                    background: "var(--surface-2)",
                    transform: open === i ? "rotate(45deg)" : "rotate(0deg)",
                  }}
                >
                  <Plus aria-hidden="true" size={13} strokeWidth={1.9} absoluteStrokeWidth />
                </span>
              </button>
              {t(open === i && (
                <div className="px-6 pb-5">
                  <p className="text-sm leading-relaxed" style={{ color: "var(--muted)", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t(item.a)}</p>
                </div>
              ))}
            </div>
          )))}
        </div>
        <div className="text-center mt-8">
          <Link href="/faq" className="text-sm font-semibold text-brand hover:underline inline-flex items-center gap-1.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("View all questions")}<ArrowRight aria-hidden="true" size={14} strokeWidth={1.8} absoluteStrokeWidth /></Link>
        </div>
      </div>
    </section>
  );
}

/* ── Final CTA ───────────────────────────────────────────── */

function FinalCTA() {
  const t = usePublicT();
  return (
    <section className="py-32 relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "linear-gradient(135deg, #4D00D3 0%, #6B21FF 50%, #8B3DFF 100%)",
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          background: "radial-gradient(ellipse 70% 50% at 30% 50%, rgba(255,255,255,0.15) 0%, transparent 60%)",
        }}
      />
      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative reveal">
        <p className="text-sm font-semibold uppercase tracking-widest mb-6 text-white/60" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: "0.15em" }}>{t("AI Local Growth Platform")}</p>
        <h2 className="text-5xl md:text-6xl font-bold mb-6 text-white leading-tight" style={{ fontFamily: "'Sora', sans-serif" }}>{t("Your business deserves")}<br />{t("to be found.")}</h2>
        <p className="text-lg text-white/75 max-w-xl mx-auto mb-10" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("Start understanding your local presence today. No complexity. No long setup. Just clear intelligence and the actions to grow.")}</p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link href="/request-access"
            className="px-8 py-4 rounded-xl text-base font-semibold transition-all hover:translate-y-[-2px]"
            style={{
              background: "white",
              color: "#4D00D3",
              boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >{t("Request access")}</Link>
          <Link href="/book-demo"
            className="px-8 py-4 rounded-xl text-base font-semibold transition-all"
            style={{
              background: "rgba(255,255,255,0.12)",
              color: "white",
              border: "1px solid rgba(255,255,255,0.25)",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >{t("Book a demo")}</Link>
        </div>
      </div>
    </section>
  );
}

/* ── Hero ────────────────────────────────────────────────── */

function Hero() {
  const t = usePublicT();
  return (
    <section className="relative min-h-screen flex items-center pt-16 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          style={{
            position: "absolute",
            top: "10%",
            left: "50%",
            transform: "translateX(-50%)",
            width: "900px",
            height: "600px",
            background: "radial-gradient(ellipse at center, rgba(77,0,211,0.09) 0%, rgba(77,0,211,0.03) 50%, transparent 80%)",
            filter: "blur(40px)",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "30%",
            right: "-10%",
            width: "500px",
            height: "500px",
            background: "radial-gradient(ellipse at center, rgba(139,61,255,0.06) 0%, transparent 70%)",
            filter: "blur(60px)",
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 md:py-20 w-full">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left: copy */}
          <div>
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest mb-8 animate-slide-up"
              style={{
                background: "rgba(77,0,211,0.08)",
                color: "#4D00D3",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                letterSpacing: "0.12em",
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full animate-pulse-brand" style={{ background: "#4D00D3" }} />{t("AI Local Growth Platform")}</div>

            <h1
              className="text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] mb-6 animate-slide-up"
              style={{
                fontFamily: "'Sora', sans-serif",
                color: "var(--foreground)",
                animationDelay: "0.1s",
                letterSpacing: "-0.02em",
              }}
            >{t("Turn local visibility")}<br />{t("into measurable")}{t(" ")}
              <span className="text-gradient">{t("growth.")}</span>
            </h1>

            <p
              className="text-lg md:text-xl leading-relaxed mb-8 max-w-md animate-slide-up"
              style={{
                color: "var(--muted)",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                animationDelay: "0.2s",
              }}
            >{t("Reviews, Google performance, Local Score and AI-prioritized next actions — one workspace built to help local businesses grow with clarity.")}</p>

            <div className="flex flex-col sm:flex-row gap-3 mb-8 sm:mb-12 animate-slide-up" style={{ animationDelay: "0.3s" }}>
              <Link href="/request-access" className="btn-primary text-base px-7 py-3.5 flex items-center justify-center gap-2">{t("Request access")}<ArrowRight aria-hidden="true" size={14} strokeWidth={1.8} absoluteStrokeWidth />
              </Link>
              <Link href="/book-demo" className="btn-ghost text-base px-7 py-3.5 flex items-center justify-center gap-2">{t("Book a demo")}</Link>
            </div>

            {/* Real local-business photography */}
            <div className="hidden sm:flex items-center gap-4 animate-slide-up" style={{ animationDelay: "0.4s" }}>
              <div className="flex -space-x-2">
                {t(LOCAL_BUSINESS_PHOTOS.map((photo) => (
                  <div
                    key={photo.src}
                    className="w-10 h-10 rounded-xl overflow-hidden border-2"
                    style={{ borderColor: "var(--background)", boxShadow: "0 3px 12px rgba(0,0,0,0.12)" }}
                  >
                    <img src={photo.src} alt={t(photo.alt)} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                )))}
              </div>
              <div>
                <div className="text-xs font-semibold mb-0.5" style={{ color: "var(--foreground)", fontFamily: "'Sora', sans-serif" }}>{t("Designed for real local businesses")}</div>
                <p className="text-xs" style={{ color: "var(--muted)", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("Restaurants, salons, hotels, clinics and retail.")}</p>
              </div>
            </div>
          </div>

          {/* Right: product preview aligned with the real client workspace */}
          <div className="animate-scale-in" style={{ animationDelay: "0.2s" }}>
            <ProductPreview />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Home page ───────────────────────────────────────────── */

export default function Home() {
  return (
    <PageShell>
      <Hero />
      <OutcomesSection />
      <EcosystemSection />
      <FeaturePillars />
      <TransformationSection />
      <AISection />
      <PricingPreview />
      <IndustriesPreview />
      <TrustSection />
      <FAQPreview />
      <FinalCTA />
    </PageShell>
  );
}
